/**
 * PublicAdvisorPage
 * Public-facing advisor profile page that visitors can view
 */

import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { Card, CardContent, Badge } from "@/shared/components/ui";
import {
  User,
  MapPin,
  Clock,
  Phone,
  Mail,
  Award,
  Building2,
  Calendar,
  Star,
  Users,
  FileText,
  CheckCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import type { AdvisorPublicProfile, ContactPreferences, CreditType } from "@/shared/types";

const CREDIT_TYPE_LABELS: Record<CreditType, string> = {
  hipoteczny: "Hipoteczny",
  gotowkowy: "Gotówkowy",
  konsolidacyjny: "Konsolidacyjny",
};

interface AdvisorData {
  name: string;
  company?: string;
  email: string;
  phone?: string;
}

export function PublicAdvisorPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["publicAdvisor", slug],
    queryFn: () => api.getAdvisorPublicProfile(slug!),
    enabled: !!slug,
  });

  const advisor = data?.advisor as AdvisorData | undefined;
  const profile = data?.profile as (AdvisorPublicProfile & { contactPreferences?: ContactPreferences }) | undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !advisor || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Profil nie znaleziony
          </h1>
          <p className="text-gray-600">
            Doradca o podanym adresie nie istnieje lub profil jest ukryty.
          </p>
        </div>
      </div>
    );
  }

  const contactPrefs = profile.contactPreferences;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              {profile.photoUrl ? (
                <img
                  src={profile.photoUrl}
                  alt={advisor.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 md:w-16 md:h-16 text-white/80" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{advisor.name}</h1>
              {profile.headline && (
                <p className="text-lg text-white/90 mb-4">{profile.headline}</p>
              )}
              {advisor.company && (
                <div className="flex items-center gap-2 text-white/80 mb-4">
                  <Building2 className="w-4 h-4" />
                  <span>{advisor.company}</span>
                </div>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-4 md:gap-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-white/70" />
                  <span>
                    <strong>{profile.yearsOfExperience}</strong> lat doświadczenia
                  </span>
                </div>
                {profile.clientsServed && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-white/70" />
                    <span>
                      <strong>{profile.clientsServed}+</strong> klientów
                    </span>
                  </div>
                )}
                {profile.creditsFinalized && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-white/70" />
                    <span>
                      <strong>{profile.creditsFinalized}+</strong> kredytów
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Bio */}
            {profile.bio && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">O mnie</h2>
                  <p className="text-gray-600 whitespace-pre-line">{profile.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Specializations */}
            {profile.specializations.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5 text-primary-600" />
                    Specjalizacje
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.specializations.map((type) => (
                      <Badge key={type} variant="info" className="text-sm">
                        Kredyt {CREDIT_TYPE_LABELS[type]}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Target Clients */}
            {profile.targetClients.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary-600" />
                    Dla kogo pracuję
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.targetClients.map((client) => (
                      <Badge key={client} variant="default">
                        {client}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certificates */}
            {profile.certificates.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary-600" />
                    Certyfikaty i uprawnienia
                  </h2>
                  <div className="space-y-3">
                    {profile.certificates.map((cert, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">{cert.name}</p>
                          <p className="text-sm text-gray-600">
                            {cert.issuedBy} ({cert.year})
                          </p>
                        </div>
                        {cert.isVerified && (
                          <Badge variant="success" className="ml-auto">
                            Zweryfikowany
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Partner Banks */}
            {profile.partnerBanks.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-600" />
                    Współpracuję z bankami
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.partnerBanks.map((bank) => (
                      <Badge key={bank} variant="default">
                        {bank}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="sticky top-4">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Kontakt</h2>

                <div className="space-y-4 mb-6">
                  {advisor.phone && (
                    <a
                      href={`tel:${advisor.phone}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600"
                    >
                      <Phone className="w-5 h-5" />
                      <span>{advisor.phone}</span>
                    </a>
                  )}
                  <a
                    href={`mailto:${advisor.email}`}
                    className="flex items-center gap-3 text-gray-600 hover:text-primary-600"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="break-all">{advisor.email}</span>
                  </a>
                </div>

                {/* Contact Preferences */}
                {contactPrefs && (
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-700">Dostępność</p>
                        <p>
                          Pon-Pt: {contactPrefs.availableHours.weekdays.from} -{" "}
                          {contactPrefs.availableHours.weekdays.to}
                        </p>
                        {contactPrefs.availableHours.saturday && (
                          <p>
                            Sob: {contactPrefs.availableHours.saturday.from} -{" "}
                            {contactPrefs.availableHours.saturday.to}
                          </p>
                        )}
                      </div>
                    </div>
                    {contactPrefs.responseTime && (
                      <p className="text-sm text-gray-600">
                        Czas odpowiedzi: {contactPrefs.responseTime}
                      </p>
                    )}
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="space-y-2 mt-6">
                  {advisor.phone && (
                    <a
                      href={`tel:${advisor.phone}`}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Zadzwoń teraz
                    </a>
                  )}
                  <a
                    href={`mailto:${advisor.email}?subject=Zapytanie o kredyt`}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Wyślij email
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Regions */}
            {profile.regions.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary-600" />
                    Regiony działania
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.regions.map((region) => (
                      <Badge key={region} variant="default">
                        {region}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-600">
          <p>
            Profil doradcy na platformie PoKredycie.pl
          </p>
        </div>
      </div>
    </div>
  );
}
